import { useNavigate } from 'react-router-dom';
import { fmtDag, fmtUren, taakStatus } from '@ep/core';
import { api } from '../lib/api';
import { useActie, useData, useMag, useTaakOpenen, type TaakRij } from '../lib/data';
import { VeldCel } from './EigenVelden';
import { Badge, VerwijderKnop } from './ui';

export function urenVan(t: TaakRij) {
  if (t.status !== 'GEPLAND') return (t.urenPerDag ?? 0) * Math.max(1, t.dagen);
  return (t.urenPerDag ?? 0) * (Math.max(1, t.dagen) - 1) + (t.urenLaatsteDag ?? t.urenPerDag ?? 0);
}

export function TaakStatusBadge({ t }: { t: TaakRij }) {
  const { data: d } = useData();
  const s = taakStatus(t, d?.vandaag ?? '');
  const tip = [
    t.terugmelding && `Teruggemeld${t.tmDatum ? ' op ' + new Date(t.tmDatum).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : ''}`,
    t.tmOpmerking,
    t.tmRestUren != null && `Nog ${fmtUren(t.tmRestUren)} uur nodig`,
    t.tmVoorkeur && `Voorkeursdatum: ${fmtDag(t.tmVoorkeur, 'jaar')}`,
    t.outlookMelding,
    t.sync === 'FOUT' && `Outlook-fout: ${t.syncFout}`,
    t.sync === 'NODIG' && 'Wordt naar Outlook gestuurd…',
  ]
    .filter(Boolean)
    .join('\n');
  return (
    <span className="rij" style={{ gap: 4 }}>
      <Badge toon={s.toon} title={tip || undefined}>
        {s.label}
      </Badge>
      {t.sync === 'NODIG' && <span title="Wordt naar Outlook gestuurd">⏳</span>}
      {t.sync === 'FOUT' && <span title={t.syncFout ?? ''}>⚠️</span>}
    </span>
  );
}

export function TakenTabel({ taken, toonProject = true }: { taken: TaakRij[]; toonProject?: boolean }) {
  const { data: d } = useData();
  const mag = useMag();
  const nav = useNavigate();
  const taak = useTaakOpenen();
  const verwijder = useActie((id: string) => api.del(`/api/taken/${id}`), { succes: 'Taak verwijderd' });
  const vervolg = useActie((id: string) => api.post<{ id: string }>(`/api/taken/${id}/vervolg`), { succes: 'Vervolgtaak aangemaakt. Plan hem nu in.', opNa: (r) => taak.open(`/taken/${r.id}`) });
  if (!d) return null;
  const eng = (id?: string | null) => d.engineers.find((e) => e.id === id)?.naam ?? '';
  // SPEC §19: eigen velden als kolom
  const kolommen = (d.eigenVelden ?? []).filter((v) => v.entiteit === 'TAAK' && v.kolom).sort((a, b) => a.volgorde - b.volgorde);
  return (
    <table className="tabel">
      <thead>
        <tr>
          {toonProject && <th>Project</th>}
          <th>Change</th>
          {toonProject && <th>Klant</th>}
          <th>Taak</th>
          <th>Engineer</th>
          <th>Datum</th>
          <th>Tijd</th>
          <th className="rechts">Uren</th>
          {kolommen.map((k) => <th key={k.id}>{k.naam}</th>)}
          <th>Status</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {taken.map((t) => (
          <tr key={t.id} className="klik" onClick={() => mag('taken.aanmaken') && taak.open(`/taken/${t.id}`)}>
            {toonProject && <td className="nowrap">{t.projectnummer ?? t.soNr ?? '–'}</td>}
            <td className="nowrap">{t.changeNr ?? '–'}</td>
            {toonProject && <td>{t.klant}</td>}
            <td>
              {t.taakType === 'SERVICE_CALL' ? '🛠 ' : t.taakType === 'OVERLEG' ? '💬 ' : t.taakType === 'FOCUS' ? '🎯 ' : ''}
              {t.titel}
              {t.werkvorm === 'ONSITE' && t.reisMin ? <span className="klein mute"> · 🚗 {Math.round(t.reisMin)}m</span> : null}
            </td>
            <td className="nowrap">{eng(t.engineerId) || <span className="mute">–</span>}</td>
            <td className="nowrap">
              {t.startdatum ? fmtDag(t.startdatum) : '–'}
              {t.einddatum && t.einddatum !== t.startdatum ? ` – ${fmtDag(t.einddatum)}` : ''}
            </td>
            <td className="nowrap">{t.van && t.status === 'GEPLAND' ? `${t.van}–${t.tot}` : ''}</td>
            <td className="rechts">{fmtUren(urenVan(t))}</td>
            {kolommen.map((k) => <td key={k.id} className="klein"><VeldCel def={k} waarden={t.velden} /></td>)}
            <td>
              <TaakStatusBadge t={t} />
            </td>
            <td className="nowrap" onClick={(e) => e.stopPropagation()}>
              {mag('taken.plannen') && !t.tmAfgehandeld && (t.terugmelding === 'NIET_KLAAR' || t.terugmelding === 'HERPLANNEN') && (
                <button className="knop klein" onClick={() => vervolg.mutate(t.id)}>
                  + Vervolg
                </button>
              )}
              {mag('taken.plannen') && <VerwijderKnop klein label="🗑" onBevestig={() => verwijder.mutate(t.id)} />}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
